import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import userService from '../services/user.service';
import { User, UpdateUserData } from '../types';

interface TeacherFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  role: 'teacher';
}

const Teachers: React.FC = () => {
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<User | null>(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{ open: boolean; teacherId?: number }>({ open: false });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; teacherId?: number }>({ open: false });

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm<TeacherFormData>();

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers('teacher');
      setTeachers(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des professeurs:', error);
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = () => {
    setEditingTeacher(null);
    reset({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'teacher',
    });
    setOpenDialog(true);
  };

  const handleEditTeacher = (teacher: User) => {
    setEditingTeacher(teacher);
    setValue('username', teacher.username);
    setValue('email', teacher.email);
    setValue('first_name', teacher.first_name);
    setValue('last_name', teacher.last_name);
    setValue('role', 'teacher');
    setOpenDialog(true);
  };

  const handleDeleteTeacher = async () => {
    if (deleteDialog.teacherId) {
      try {
        await userService.deleteUser(deleteDialog.teacherId);
        await loadTeachers();
        setDeleteDialog({ open: false });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleToggleStatus = async (teacherId: number, currentStatus: boolean) => {
    try {
      await userService.toggleUserStatus(teacherId, !currentStatus);
      await loadTeachers();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    }
  };

  const handleResetPassword = async () => {
    if (resetPasswordDialog.teacherId) {
      const newPassword = `temp${Math.random().toString(36).substring(2, 10)}`;
      try {
        await userService.resetPassword(resetPasswordDialog.teacherId, newPassword);
        alert(`Nouveau mot de passe temporaire: ${newPassword}`);
        setResetPasswordDialog({ open: false });
      } catch (error) {
        console.error('Erreur lors de la réinitialisation:', error);
      }
    }
  };

  const onSubmit = async (data: TeacherFormData) => {
    try {
      if (editingTeacher) {
        const updateData: UpdateUserData = {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
        };
        await userService.updateUser(editingTeacher.id, updateData);
      } else {
        await userService.createUser({ ...data, role: 'teacher', password: data.password! });
      }
      await loadTeachers();
      setOpenDialog(false);
      reset();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Nom d\'utilisateur', width: 150 },
    { field: 'first_name', headerName: 'Prénom', width: 130 },
    { field: 'last_name', headerName: 'Nom', width: 130 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'is_active',
      headerName: 'Statut',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Actif' : 'Inactif'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Créé le',
      width: 150,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'dd/MM/yyyy', { locale: fr });
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Modifier"
          onClick={() => handleEditTeacher(row)}
        />,
        <GridActionsCellItem
          icon={row.is_active ? <CloseIcon /> : <CheckIcon />}
          label={row.is_active ? 'Désactiver' : 'Activer'}
          onClick={() => handleToggleStatus(row.id, row.is_active)}
        />,
        <GridActionsCellItem
          icon={<LockIcon />}
          label="Réinitialiser mot de passe"
          onClick={() => setResetPasswordDialog({ open: true, teacherId: row.id })}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Supprimer"
          onClick={() => setDeleteDialog({ open: true, teacherId: row.id })}
          color="error"
        />,
      ],
    },
  ];

  const filteredTeachers = teachers.filter(teacher =>
    searchQuery === '' ||
    teacher.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.last_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestion des Professeurs</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTeacher}
        >
          Nouveau Professeur
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par nom, email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredTeachers}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Dialog Création/Modification */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingTeacher ? 'Modifier le professeur' : 'Nouveau professeur'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Nom d'utilisateur"
                {...register('username', { required: 'Requis' })}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={!!editingTeacher}
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                {...register('email', { 
                  required: 'Requis',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email invalide'
                  }
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
              />
              <TextField
                label="Prénom"
                {...register('first_name', { required: 'Requis' })}
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
                fullWidth
              />
              <TextField
                label="Nom"
                {...register('last_name', { required: 'Requis' })}
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
                fullWidth
              />
              {!editingTeacher && (
                <TextField
                  label="Mot de passe"
                  type="password"
                  {...register('password', { 
                    required: editingTeacher ? false : 'Requis',
                    minLength: {
                      value: 6,
                      message: 'Minimum 6 caractères'
                    }
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  fullWidth
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editingTeacher ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Réinitialisation mot de passe */}
      <Dialog open={resetPasswordDialog.open} onClose={() => setResetPasswordDialog({ open: false })}>
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir réinitialiser le mot de passe de ce professeur ?
            Un nouveau mot de passe temporaire sera généré.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog({ open: false })}>Annuler</Button>
          <Button onClick={handleResetPassword} variant="contained" color="warning">
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Suppression */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>Supprimer le professeur</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Cette action est irréversible. Toutes les données associées à ce professeur seront supprimées.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>Annuler</Button>
          <Button onClick={handleDeleteTeacher} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Teachers;